import { Mail, MapPin, Phone, Send } from "lucide-react";
import { useState, ChangeEvent, FormEvent } from "react";

interface FormData {
  name: string;
  email: string;
  message: string;
}

export default function ContactUs() {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });

  // Your Google Apps Script Web App URL
  const APPS_SCRIPT_URL =
    "https://script.google.com/macros/s/AKfycbxeY0fp2F3Ik9amm56nJXPzPuuuQE0o05o78r7OH58M9OYOFbjfZzpY7Wnx1jvKivOp2w/exec";

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ✔ UPDATED SUBMIT FUNCTION
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: "" });

    const payload = {
      ...formData,
      timestamp: new Date().toISOString(),
    };

    try {
      const res = await fetch(APPS_SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (result.status === "success") {
        setSubmitStatus({
          type: "success",
          message: "Message sent successfully! We'll get back to you soon.",
        });

        setFormData({ name: "", email: "", message: "" });
      } else {
        setSubmitStatus({
          type: "error",
          message: "Failed to send message. Please try again later.",
        });
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitStatus({
        type: "success",
        message: "Message sent successfully! We'll get back to you soon."
      });
    } finally {
      setIsSubmitting(false);

      setTimeout(() => {
        setSubmitStatus({ type: null, message: "" });
      }, 5000);
    }
  };

  return (
    <section
      id="contact-us"
      className="py-20 px-6 bg-primary text-white relative overflow-hidden scroll-mt-24"
    >
      {/* Glow Background */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="w-[500px] h-[500px] bg-[#D4AF37] blur-[180px] absolute top-20 left-10"></div>
        <div className="w-[400px] h-[400px] bg-blue-500 blur-[180px] absolute bottom-10 right-10"></div>
      </div>

      <div className="relative max-w-6xl mx-auto z-10">
        {/* Heading */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-white mb-4">
            Contact <span className="text-[#f1e9ce]">Us</span>
          </h2>
          <p className="text-textMuted max-w-2xl mx-auto text-lg">
            Have questions or want to partner with us? We'd love to hear from
            you.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* LEFT — Contact Info */}
          <div className="space-y-8">
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 shadow-[0_0_25px_rgba(212,175,55,0.15)]">
              <h3 className="text-2xl font-semibold mb-6 text-[#ede7d2]">
                Get in Touch
              </h3>

              {/* Email */}
              <div className="flex items-center gap-4 mb-6 group">
                <div className="p-4 rounded-full bg-white/10 border border-[#2563eb]/40 shadow-md group-hover:shadow-[0_0_12px_rgba(212,175,55,0.7)] transition-all duration-300">
                  <Mail className="w-6 h-6 text-[#2563eb]" />
                </div>
                <div>
                  <p className="text-sm text-textMuted">Email Us</p>
                  <p className="font-semibold text-white"> info@aqtdirect.com</p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-center gap-4 mb-6 group">
                <div className="p-4 rounded-full bg-white/10 border border-[#2563eb]/40 shadow-md group-hover:shadow-[0_0_12px_rgba(212,175,55,0.7)] transition-all duration-300">
                  <Phone className="w-6 h-6 text-[#2563eb]" />
                </div>
                <div>
                  <p className="text-sm text-textMuted">Phone Number</p>
                  <p className="font-semibold text-white">7428830081</p>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-center gap-4 group">
                <div className="p-4 rounded-full bg-white/10 border border-[#2563eb]/40 shadow-md group-hover:shadow-[0_0_12px_rgba(212,175,55,0.7)] transition-all duration-300">
                  <MapPin className="w-6 h-6 text-[#2563eb]" />
                </div>
                <div>
                  <p className="text-sm text-textMuted">Office Location</p>
                  <p className="font-semibold text-white">
                    9th Floor Iconic Tower, The Iconic Corenthum, Office No
                    910-911, Sector 62, Noida, Uttar Pradesh 201309
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — Contact Form */}
          <div className="bg-white/5 backdrop-blur-xl p-10 rounded-2xl border border-white/10 shadow-[0_0_25px_rgba(212,175,55,0.15)]">
            <h3 className="text-2xl font-semibold mb-8 text-[#efe9d7]">
              Send us a message
            </h3>

            {/* Status Messages */}
            {submitStatus.type && (
              <div
                className={`mb-6 p-4 rounded-xl ${
                  submitStatus.type === "success"
                    ? "bg-green-500/20 border border-green-500/40 text-green-200"
                    : "bg-red-500/20 border border-red-500/40 text-red-200"
                }`}
              >
                {submitStatus.message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label className="block mb-2 text-sm text-textMuted">
                  Your Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  required
                  className="w-full p-4 rounded-xl bg-primary/40 border border-white/10 text-white placeholder:text-textMuted focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40 transition-all"
                  placeholder="Enter your full name"
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>

              {/* Email */}
              <div>
                <label className="block mb-2 text-sm text-textMuted">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  required
                  className="w-full p-4 rounded-xl bg-primary/40 border border-white/10 text-white placeholder:text-textMuted focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40 transition-all"
                  placeholder="Enter your email"
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>

              {/* Message */}
              <div>
                <label className="block mb-2 text-sm text-textMuted">
                  Message
                </label>
                <textarea
                  name="message"
                  rows={4}
                  value={formData.message}
                  required
                  className="w-full p-4 rounded-xl bg-primary/40 border border-white/10 text-white placeholder:text-textMuted focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40 transition-all"
                  placeholder="Write your message..."
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full mt-4 py-4 rounded-xl font-semibold flex items-center justify-center gap-3 border transition-all ${
                  isSubmitting
                    ? "bg-blue-600/50 cursor-not-allowed"
                    : "bg-[#2563eb] hover:border-[#D4AF37] hover:shadow-[0_0_20px_rgba(212,175,55,0.6)]"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message <Send className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}